// "Send to a known bad number?" guard for MANUAL SMS sends.
//
// A number is bad when public.sms_number_health says its latest text failed
// with a permanent Twilio error (invalid, landline, unreachable) — see
// get_bad_number_info in backend/twilio_service/utils.py. The backend blocks
// every send to such a number unless the request carries confirm_bad_number,
// so automated sends are always blocked. Manual sends call confirmBadNumbers()
// first: it asks the user (BadNumberConfirmHost), and the caller passes the
// answer through as confirm_bad_number, or skips the recipient.
//
// A batch (more than one recipient) only warns and skips the bad numbers — no
// Twilio spend on texts that cannot arrive. "Send anyway" is offered only
// when texting a single patient.

import { getBackendURL } from "../components/env";

// Mirror of public.sms_normalize_phone(): (555) 123-4567, 5551234567 and
// +15551234567 are the same number.
export const smsE164 = (raw) => {
  const digits = String(raw ?? "").replace(/\D/g, "");
  if (!digits) return "";
  return digits.length === 10 ? `+1${digits}` : `+${digits}`;
};

// {phone as sent: {error_code, description, last_failure_at}} for the bad
// ones. Fails open: a lookup error must never stop a send.
export const fetchBadNumbers = async (phones) => {
  const list = [...new Set((phones || []).filter(Boolean).map(String))];
  if (!list.length) return {};
  try {
    const res = await fetch(`${getBackendURL()}/twilio/check-numbers/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ phones: list }),
    });
    if (!res.ok) return {};
    const data = await res.json();
    return data?.bad || {};
  } catch (err) {
    console.warn("[badNumbers] check failed, not blocking the send:", err);
    return {};
  }
};

let openDialog = null;

// Called by BadNumberConfirmHost; the returned function unregisters it.
export const registerBadNumberDialog = (fn) => {
  openDialog = fn;
  return () => {
    if (openDialog === fn) openDialog = null;
  };
};

const noBadNumbers = { confirmed: () => false, skipped: () => false };

// Every number a recipient can be texted on: `phones: [...]` (cell + home,
// see utils/smsPhone) or the single `phone`.
const recipientPhones = (r) => {
  const list = Array.isArray(r?.phones) ? r.phones : [r?.phone];
  return [...new Set(list.filter(Boolean).map(String))];
};

/**
 * Ask before texting known-bad numbers.
 *
 * @param recipients [{ phone, name? }] or [{ phones: [...], name? }] — everyone
 *   this click is about to text; more than one is a batch, where bad numbers
 *   are always skipped. A recipient with several numbers is only a hit when
 *   EVERY one of them is bad: otherwise the send falls through to the good one.
 * @returns null if the user cancelled the whole send; otherwise a guard:
 *   guard.skipped(phone)   — leave this recipient out (any of their numbers)
 *   guard.confirmed(phone) — send with confirm_bad_number: true
 */
export const confirmBadNumbers = async (recipients) => {
  const list = (recipients || [])
    .map((r) => (r ? { ...r, phones: recipientPhones(r) } : null))
    .filter((r) => r && r.phones.length);
  const bad = await fetchBadNumbers(list.flatMap((r) => r.phones));
  const byNumber = {};
  Object.entries(bad).forEach(([phone, info]) => {
    byNumber[smsE164(phone)] = info;
  });
  const hits = list
    .filter((r) => r.phones.every((p) => byNumber[smsE164(p)]))
    .map((r) => ({
      ...byNumber[smsE164(r.phones[0])],
      name: r.name || "",
      phone: r.phones.join(" / "),
      phones: r.phones,
    }));
  if (!hits.length) return noBadNumbers;

  const batch = (recipients || []).length > 1;
  let choice;
  if (openDialog) {
    choice = await openDialog({ hits, total: list.length, batch });
  } else {
    const lines = hits.map((h) => `${h.name ? `${h.name} ` : ""}${h.phone}: ${h.description || h.error_code}`);
    const intro = `Texts to ${hits.length === 1 ? "this number" : "these numbers"} failed permanently:\n\n${lines.join("\n")}\n\n`;
    if (batch) {
      choice = window.confirm(`${intro}They will be skipped. Continue?`) ? "skip" : "cancel";
    } else {
      choice = window.confirm(`${intro}Send anyway?`) ? "send" : "cancel";
    }
  }
  if (choice === "cancel") return null;
  // A batch never texts a bad number, whatever the dialog answered.
  if (batch) choice = "skip";

  const numbers = new Set(hits.flatMap((h) => h.phones.map(smsE164)));
  return {
    confirmed: (phone) => choice === "send" && numbers.has(smsE164(phone)),
    skipped: (phone) => choice === "skip" && numbers.has(smsE164(phone)),
  };
};
